import { createApp, markRaw } from 'vue';
import App from '@/App.vue';

// Add Clarity
import Clarity from '@microsoft/clarity';

const clarityProjectId = import.meta.env.VITE_CLARITY_PROJECT_ID;
if (clarityProjectId) {
    Clarity.init(clarityProjectId);
}

const app = createApp(App);

// Add Sentry
import * as Sentry from '@sentry/vue';

const sentryDNS = import.meta.env.VITE_SENTRY_DNS;
if (sentryDNS) {
    Sentry.init({
        app,
        dsn: sentryDNS,
        sendDefaultPii: true,
        integrations: [
            Sentry.replayIntegration({
                maskAllText: false,
                blockAllMedia: false,
            })
        ],
        // Session Replay
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0
    });
}

// Add pinia and router
import { createPinia } from 'pinia';
import router from '@/router';

const pinia = createPinia();
pinia.use(({ store }) => {
    store.router = markRaw(router); // Inject router into all stores
});
app.use(pinia);
app.use(router);

// main app css
import '@/assets/css/app.css';

// perfect scrollbar
import { PerfectScrollbarPlugin } from 'vue3-perfect-scrollbar';

app.use(PerfectScrollbarPlugin);

//vue-meta
import { createHead } from '@vueuse/head';

const head = createHead();
app.use(head);

// set default settings
import appSetting from '@/app-setting';

appSetting.init();

//vue-i18n
import i18n from '@/i18n';

app.use(i18n);

// tippy tooltips
import { TippyPlugin } from 'tippy.vue';

app.use(TippyPlugin);

//input mask
import { vMaska } from 'maska/vue';

app.directive('maska', vMaska);

//markdown editor
import VueEasymde from 'vue3-easymde';
import 'easymde/dist/easymde.min.css';

app.use(VueEasymde);

// popper
import Popper from 'vue3-popper';

app.component('Popper', Popper);

// json to excel
import vue3JsonExcel from 'vue3-json-excel';

app.use(vue3JsonExcel);

// PrimeVue
import PrimeVue from 'primevue/config';
import { definePreset } from '@primevue/themes';
import Aura from '@primevue/themes/aura';
import Select from 'primevue/select';
import InputNumber from 'primevue/inputnumber';
import InputGroup from 'primevue/inputgroup';
import InputGroupAddon from 'primevue/inputgroupaddon';
import Toast from 'primevue/toast';
import InputText from 'primevue/inputtext';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import Message from 'primevue/message';
import Button from 'primevue/button';
import Password from 'primevue/password';
import DatePicker from 'primevue/datepicker';
import AutoComplete from 'primevue/autocomplete';
import Divider from 'primevue/divider';
import ToggleSwitch from 'primevue/toggleswitch';
import Checkbox from 'primevue/checkbox';
import Skeleton from 'primevue/skeleton';
import FileUpload from 'primevue/fileupload';
import ConfirmPopup from 'primevue/confirmpopup';
import ConfirmationService from 'primevue/confirmationservice';
import RadioButton from 'primevue/radiobutton';
import Tooltip from 'primevue/tooltip';
import KeyFilter from 'primevue/keyfilter';
import Tree from 'primevue/tree';
import ConfirmDialog from 'primevue/confirmdialog';
import SpeedDial from 'primevue/speeddial';
import Menubar from 'primevue/menubar';
import Chip from 'primevue/chip';
import Popover from 'primevue/popover';
import Menu from 'primevue/menu';
import Fieldset from 'primevue/fieldset';
import ColorPicker from 'primevue/colorpicker';
import Textarea from 'primevue/textarea';
import MultiSelect from 'primevue/multiselect';
import DynamicDialog from 'primevue/dynamicdialog';
import Tag from 'primevue/tag';
import Paginator from 'primevue/paginator';
import TreeSelect from 'primevue/treeselect';
import DialogService from 'primevue/dialogservice';

const MyPreset = definePreset(Aura, {
    semantic: {
        primary: {
            50: '#f0f4ff',
            100: '#e0e7ff',
            200: '#c7d2fe',
            300: '#a5b4fc',
            400: '#6366f1',
            500: '#4361ee',  // Your design's primary color
            600: '#3b56db',  // Better hover color (slightly darker)
            700: '#334ac0',
            800: '#2d3a9f',
            900: '#1e2875',
            950: '#131a4a'
        }
    },
    components: {
        tag: {
            font: {
                size: '0.75rem',
                weight: '500'
            },
            padding: '0.125rem 0.5rem'
        }
    }
});
app.use(PrimeVue, {
    theme: {
        preset: MyPreset,
        options: {
            darkModeSelector: false
        }
    },
    locale: {
        firstDayOfWeek: 6,
        dayNames: ["الأحد", "الاثنين", "الثلاثاء", "الأربعاء", "الخميس", "الجمعة", "السبت"],
        dayNamesShort: ["أحد", "اثن", "ثلث", "أرب", "خميس", "جمع", "سبت"],
        dayNamesMin: ["ح", "ن", "ث", "ر", "خ", "ج", "س"],
        monthNames: [
            "يناير",
            "فبراير",
            "مارس",
            "أبريل",
            "مايو",
            "يونيو",
            "يوليو",
            "أغسطس",
            "سبتمبر",
            "أكتوبر",
            "نوفمبر",
            "ديسمبر",
        ],
        emptySearchMessage: 'لا يوجد نتائج'
    }
});
import ToastService from 'primevue/toastservice';

app.use(ToastService);
app.component('Select', Select);
app.component('InputNumber', InputNumber);
app.component('InputGroup', InputGroup);
app.component('InputGroupAddon', InputGroupAddon);
app.component('Toast', Toast);
app.component('InputText', InputText);
app.component('IconField', IconField);
app.component('InputIcon', InputIcon);
app.component('Message', Message);
app.component('Button', Button);
app.component('Password', Password);
app.component('DatePicker', DatePicker);
app.component('AutoComplete', AutoComplete);
app.component('Divider', Divider);
app.component('ToggleSwitch', ToggleSwitch);
app.component('Checkbox', Checkbox);
app.component('Skeleton', Skeleton);
app.component('FileUpload', FileUpload);
app.component('ConfirmPopup', ConfirmPopup);
app.component('RadioButton', RadioButton);
app.component('Tree', Tree);
app.component('ConfirmDialog', ConfirmDialog);
app.component('SpeedDial', SpeedDial);
app.component('Menubar', Menubar);
app.component('Chip', Chip);
app.component('Popover', Popover);
app.component('Menu', Menu);
app.component('Fieldset', Fieldset);
app.component('ColorPicker', ColorPicker);
app.component('Textarea', Textarea);
app.component(Tag.name, Tag);
app.component(Paginator.name, Paginator);
app.component(MultiSelect.name, MultiSelect);
app.component(DynamicDialog.name, DynamicDialog);
app.component(TreeSelect.name, TreeSelect);
app.use(ConfirmationService);
app.use(DialogService);
app.directive('tooltip', Tooltip);
app.directive('keyfilter', KeyFilter);

app.mount('#app');
